// src/components/Home.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TemplateCard from "./TemplateCard";
import { Button } from "./ui/button";
import {
    ArrowRight,
    Sparkles,
    LayoutTemplate,
    BarChart3,
    Tags as TagsIcon,
    Clock,
    Flame,
    Plus,
    Heart,
} from "lucide-react";

function Home() {
    const [latestTemplates, setLatestTemplates] = useState([]);
    const [topTemplates, setTopTemplates] = useState([]);
    const [tags, setTags] = useState([]);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    const isAuthenticated = useMemo(() => !!localStorage.getItem("token"), []);
    const currentUser = useMemo(() => {
        try {
            const raw = localStorage.getItem("user");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        const fetchHomeData = async () => {
            try {
                setError(null);

                let resp = await fetch(`${API_URL}/api/templates/latest`);
                if (!resp.ok) throw new Error("Failed to fetch latest templates");
                let data = await resp.json();
                setLatestTemplates(Array.isArray(data) ? data : []);

                resp = await fetch(`${API_URL}/api/templates/top`);
                if (!resp.ok) throw new Error("Failed to fetch top templates");
                data = await resp.json();
                setTopTemplates(Array.isArray(data) ? data : []);

                resp = await fetch(`${API_URL}/api/tags/cloud`);
                if (!resp.ok) throw new Error("Failed to fetch tag cloud");
                data = await resp.json();
                setTags(Array.isArray(data) ? data : []);
            } catch (err) {
                setError(err?.message || "Something went wrong");
            }
        };

        if (API_URL) fetchHomeData();
    }, [API_URL]);

    const handleTagClick = (tagId) => {
        navigate(`/search-results?q=${tagId}&type=tag`);
    };

    const handleLike = async (templateId, isFromLatest) => {
        if (!isAuthenticated) {
            setError("You must be logged in to like a template");
            return;
        }

        const token = localStorage.getItem("token");
        try {
            const response = await fetch(`${API_URL}/api/likes`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ template_id: templateId }),
            });

            if (!response.ok) throw new Error("Failed to like template");

            const bumpLike = (arr) =>
                arr.map((t) =>
                    t.id === templateId ? { ...t, likeCount: (t.likeCount || 0) + 1 } : t
                );

            if (isFromLatest) setLatestTemplates((prev) => bumpLike(prev));
            else setTopTemplates((prev) => bumpLike(prev));
        } catch (err) {
            setError(err?.message || "Failed to like template");
        }
    };

    // Derived stats for the hero strip
    const totalSubmissions = useMemo(
        () => topTemplates.reduce((sum, t) => sum + (t.forms_count || 0), 0),
        [topTemplates]
    );

    const features = [
        {
            icon: LayoutTemplate,
            title: "Flexible templates",
            text: "Mix text, numbers, checkboxes, and images. Public or private — your call.",
        },
        {
            icon: BarChart3,
            title: "Live analytics",
            text: "Averages, common answers, and submission counts computed automatically.",
        },
        {
            icon: TagsIcon,
            title: "Discoverable",
            text: "Tag cloud, search, and likes surface the templates people actually use.",
        },
    ];

    return (
        <div className="space-y-14">
            {/* ===================== HERO ===================== */}
            <section className="relative overflow-hidden rounded-3xl border border-zinc-200/70 bg-white/70 p-8 shadow-card backdrop-blur-sm animate-fade-in sm:p-12 dark:border-zinc-800/70 dark:bg-zinc-900/40">
                <div className="bg-dots pointer-events-none absolute inset-0 opacity-60" />
                <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-gradient opacity-20 blur-3xl" />

                <div className="relative max-w-2xl">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:border-brand-800/60 dark:bg-brand-950/50 dark:text-brand-200">
                        <Sparkles className="h-3.5 w-3.5" />
                        {isAuthenticated ? "Welcome back" : "Build forms in minutes"}
                    </span>

                    <h1 className="mt-5 text-4xl font-extrabold leading-[1.1] tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-50">
                        {isAuthenticated && currentUser?.username ? (
                            <>
                                Hi {currentUser.username},{" "}
                                <span className="text-gradient">let's build something.</span>
                            </>
                        ) : (
                            <>
                                Forms that <span className="text-gradient">forge results.</span>
                            </>
                        )}
                    </h1>

                    <p className="mt-4 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg dark:text-zinc-300">
                        Create dynamic templates, collect submissions, and turn responses into
                        insight — all in one clean, fast workspace.
                    </p>

                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        {isAuthenticated ? (
                            <>
                                <Button size="lg" onClick={() => navigate("/create-template")}>
                                    <Plus className="h-5 w-5" />
                                    Create a template
                                </Button>
                                <Button
                                    variant="outline"
                                    size="lg"
                                    onClick={() => navigate("/templates")}
                                >
                                    Your templates <ArrowRight className="h-4 w-4" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button size="lg" onClick={() => navigate("/register")}>
                                    Get started free <ArrowRight className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="lg"
                                    onClick={() => navigate("/public-templates")}
                                >
                                    Browse templates
                                </Button>
                            </>
                        )}
                    </div>

                    {/* Stat strip */}
                    <div className="mt-9 flex flex-wrap gap-x-10 gap-y-4">
                        <Stat value={latestTemplates.length || topTemplates.length || 0} label="Templates live" />
                        <Stat value={totalSubmissions} label="Submissions collected" />
                        <Stat value={tags.length} label="Topics tagged" />
                    </div>
                </div>

                {error && (
                    <div className="relative mt-6 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
                        {error}
                    </div>
                )}
            </section>

            {/* ===================== FEATURES ===================== */}
            <section className="grid gap-5 sm:grid-cols-3">
                {features.map(({ icon: Icon, title, text }) => (
                    <div
                        key={title}
                        className="group rounded-2xl border border-zinc-200/70 bg-white/60 p-6 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-brand-300 hover:shadow-card-hover dark:border-zinc-800/70 dark:bg-zinc-900/40 dark:hover:border-brand-700"
                    >
                        <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-gradient text-white shadow-soft transition-transform duration-300 group-hover:scale-110">
                            <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="mt-4 text-base font-semibold tracking-tight">{title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                            {text}
                        </p>
                    </div>
                ))}
            </section>

            {/* ===================== LATEST ===================== */}
            <SectionHeader
                icon={Clock}
                title="Latest templates"
                subtitle="Freshly published by the community"
                onAction={() => navigate("/public-templates")}
            />
            {latestTemplates.length === 0 ? (
                <EmptyState text="No latest templates available yet." />
            ) : (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {latestTemplates.map((template) => (
                        <TemplateCard
                            key={template.id}
                            template={template}
                            showAuthor
                            isAuthenticated={isAuthenticated}
                            onLike={() => handleLike(template.id, true)}
                            viewHref={`/templates/${template.id}`}
                            fillHref={`/submit-form/${template.id}`}
                        />
                    ))}
                </div>
            )}

            {/* ===================== TOP ===================== */}
            <SectionHeader
                icon={Flame}
                title="Top templates"
                subtitle="Most liked and most filled out"
                onAction={() => navigate("/public-templates")}
            />
            {topTemplates.length === 0 ? (
                <EmptyState text="No top templates available yet." />
            ) : (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {topTemplates.map((template) => (
                        <TemplateCard
                            key={template.id}
                            template={template}
                            showFormsFilled
                            isAuthenticated={isAuthenticated}
                            onLike={() => handleLike(template.id, false)}
                            viewHref={`/templates/${template.id}`}
                            fillHref={`/submit-form/${template.id}`}
                        />
                    ))}
                </div>
            )}

            {/* ===================== TAGS ===================== */}
            <div>
                <SectionHeader icon={TagsIcon} title="Explore by tag" subtitle="Jump into a topic" />
                {tags.length === 0 ? (
                    <EmptyState text="No tags yet." />
                ) : (
                    <div className="flex flex-wrap gap-2.5">
                        {tags.map((tag) => (
                            <button
                                key={tag.id}
                                onClick={() => handleTagClick(tag.id)}
                                className="group inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/70 px-3.5 py-1.5 text-sm font-medium text-zinc-700 transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-zinc-200 dark:hover:border-brand-700 dark:hover:bg-brand-950/50 dark:hover:text-brand-200"
                                title={`View templates for tag: ${tag.name}`}
                            >
                                #{tag.name}
                                <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-600 transition-colors group-hover:bg-brand-100 group-hover:text-brand-700 dark:bg-zinc-800 dark:text-zinc-300 dark:group-hover:bg-brand-900 dark:group-hover:text-brand-100">
                                    {tag.count ?? 0}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* ===================== CTA (logged-out) ===================== */}
            {!isAuthenticated && (
                <section className="relative overflow-hidden rounded-3xl bg-brand-gradient p-10 text-center shadow-card-hover sm:p-14">
                    <div className="bg-dots pointer-events-none absolute inset-0 opacity-20" />
                    <div className="relative mx-auto max-w-xl">
                        <Heart className="mx-auto h-8 w-8 text-white/90" />
                        <h2 className="mt-4 text-2xl font-bold tracking-tight text-white sm:text-3xl">
                            Ready to build your first form?
                        </h2>
                        <p className="mt-2 text-white/85">
                            Free to start. No credit card. Launch a template in under a minute.
                        </p>
                        <div className="mt-6 flex justify-center">
                            <Button
                                size="lg"
                                onClick={() => navigate("/register")}
                                className="bg-white text-brand-700 shadow-soft hover:bg-zinc-50 hover:brightness-100"
                            >
                                Create your account <ArrowRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}

/* ---------- small presentational helpers ---------- */

function Stat({ value, label }) {
    return (
        <div>
            <div className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
                {value}
            </div>
            <div className="text-sm text-zinc-500 dark:text-zinc-400">{label}</div>
        </div>
    );
}

function SectionHeader({ icon: Icon, title, subtitle, onAction }) {
    return (
        <div className="mb-5 flex items-end justify-between gap-4">
            <div className="flex items-center gap-3">
                {Icon && (
                    <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-950/50 dark:text-brand-300">
                        <Icon className="h-5 w-5" />
                    </span>
                )}
                <div>
                    <h2 className="text-xl font-bold tracking-tight">{title}</h2>
                    {subtitle && (
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">{subtitle}</p>
                    )}
                </div>
            </div>
            {onAction && (
                <button
                    onClick={onAction}
                    className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-brand-600 transition-colors hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
                >
                    View all <ArrowRight className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}

function EmptyState({ text }) {
    return (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white/40 p-10 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900/30 dark:text-zinc-400">
            {text}
        </div>
    );
}

export default Home;
